// Temporary developer-only observability (network connectivity investigation,
// Step 3). A no-op unless FEATURES.NETWORK_DIAGNOSTICS — see the store for the full rationale.
import { useNetworkDiagnostics } from '../../zustand/network-diagnostics-store';

// Mirrors the connection's state onto `window.__realtimeConnectionDebug` so a
// stuck connection can be diagnosed from the browser console without a
// breakpoint — which stage failed is otherwise very hard to see.
const INITIAL_SNAPSHOT = {
  status: 'idle',
  sessionId: null,
  exerciseId: null,
  callId: null,
  error: null,
  hasRemoteStream: false,
  hasAudioElement: false,
  audioSignalPresent: false,
  connectionState: null,
  iceConnectionState: null,
  iceGatheringState: null,
  signalingState: null,
  stream: null,
  reconnecting: false,
  reconnectAttempt: 0,
  // Last OpenAI `error` event off the data channel, plus a per-code tally.
  // Nothing reacts to these — they exist to settle whether the backend's
  // response.create race correlates with the audio anomalies at all.
  lastOpenAiErrorCode: null,
  openAiErrorCounts: null,
  // Last tone-detector reading, in observe mode as well as heal.
  lastTonePeakHz: null,
  lastToneProminenceDb: null,
  toneAnomalyCount: 0,
  // Lateness of the monitor's sampling interval. Elevated = starved main thread.
  tickLagMs: null,
  updatedAt: null,
};

// Last N events off the OpenAI data channel. A ring buffer, not a growing list:
// a long session emits thousands and we only ever read the recent tail.
const EVENT_BUFFER_LIMIT = 200;

const getTarget = () => (typeof window === 'undefined' ? null : window);

/**
 * Verbose logging is opt-in, and deliberately readable at runtime rather than
 * build time — a silent agent usually shows up on a deployed build, where
 * flipping a `VITE_` flag would mean a rebuild.
 *
 *   localStorage.setItem('realtime-debug', '1'); location.reload();
 */
export function isRealtimeDebugEnabled() {
  const target = getTarget();
  if (!target) {
    return false;
  }

  if (target.__realtimeDebugEnabled === true) {
    return true;
  }

  try {
    return target.localStorage?.getItem('realtime-debug') === '1';
  } catch (_error) {
    // Storage can throw in a hardened/incognito context.
    return false;
  }
}

/**
 * Whether a detected tone anomaly should actually cycle playback, or only be
 * recorded.
 *
 * Ships as 'observe'. The detector's thresholds were derived from first
 * principles, not from field measurements — the previous ones were wrong in a
 * way that made the detector silently unfireable for its whole life, and the
 * failure mode of getting them wrong in the other direction is cutting audio
 * out from under a patient mid-sentence. Observe first, set the thresholds from
 * real distributions, then flip.
 *
 * Read at runtime rather than build time for the same reason as
 * isRealtimeDebugEnabled: the interesting sessions are on deployed builds.
 *
 *   localStorage.setItem('realtime-tone-mode', 'heal'); location.reload();
 */
export function getToneDetectorMode() {
  const target = getTarget();
  if (!target) {
    return 'observe';
  }

  try {
    return target.localStorage?.getItem('realtime-tone-mode') === 'heal' ? 'heal' : 'observe';
  } catch (_error) {
    return 'observe';
  }
}

// console.warn rather than console.log: the eslint config allows warn/error only.
export function realtimeDebugLog(label, payload) {
  if (!isRealtimeDebugEnabled()) {
    return;
  }

  if (payload === undefined) {
    console.warn(`[realtime] ${label}`);
    return;
  }

  console.warn(`[realtime] ${label}`, payload);
}

export function resetRealtimeDebugSnapshot() {
  const target = getTarget();
  if (!target) {
    return;
  }
  target.__realtimeConnectionDebug = { ...INITIAL_SNAPSHOT };
  target.__realtimeEvents = [];
}

export function updateRealtimeDebugSnapshot(patch) {
  const target = getTarget();
  if (!target) {
    return;
  }

  target.__realtimeConnectionDebug = {
    ...(target.__realtimeConnectionDebug ?? INITIAL_SNAPSHOT),
    ...patch,
    updatedAt: new Date().toISOString(),
  };
}

export function describeMediaStream(stream) {
  if (!stream) {
    return null;
  }

  return {
    id: stream.id,
    active: stream.active,
    trackCount: stream.getTracks?.().length ?? 0,
    audioTracks: (stream.getAudioTracks?.() ?? []).map(track => ({
      id: track.id,
      kind: track.kind,
      label: track.label,
      enabled: track.enabled,
      muted: track.muted,
      readyState: track.readyState,
    })),
  };
}

/**
 * `paused`/`muted`/`volume` are the three things that make a perfectly healthy
 * stream inaudible, and none of them are visible from the connection state.
 */
export function describeAudioElement(element) {
  if (!element) {
    return null;
  }

  return {
    hasSrcObject: Boolean(element.srcObject),
    paused: element.paused,
    muted: element.muted,
    volume: element.volume,
    autoplay: element.autoplay,
    readyState: element.readyState,
    currentTime: element.currentTime,
    stream: describeMediaStream(element.srcObject ?? null),
  };
}

/**
 * Every event OpenAI pushes down the data channel, kept so the console can show
 * what actually arrived rather than what we expected to arrive.
 */
export function recordRealtimeEvent(event) {
  const target = getTarget();
  if (!target) {
    return;
  }

  const buffer = target.__realtimeEvents ?? [];
  buffer.push({
    at: new Date().toISOString(),
    type: event?.type ?? '(untyped)',
    event,
  });

  if (buffer.length > EVENT_BUFFER_LIMIT) {
    buffer.splice(0, buffer.length - EVENT_BUFFER_LIMIT);
  }

  target.__realtimeEvents = buffer;

  // An `error` event is why the agent goes quiet more often than anything else,
  // so it is reported whether or not verbose logging is on.
  if (event?.type === 'error') {
    console.error('[realtime] OpenAI error event', event);
    return;
  }

  realtimeDebugLog(`event ${event?.type ?? '(untyped)'}`, event);
}

// The report needs live RTC stats, which only the connection has. It registers
// a getter here rather than debug.js reaching into the store.
let peerConnectionProvider = null;
let audioElementProvider = null;
let audioLevelProvider = null;

export function registerRealtimeDebugProviders({
  getPeerConnection,
  getAudioElement,
  getAudioLevel,
}) {
  if (getPeerConnection !== undefined) {
    peerConnectionProvider = getPeerConnection;
  }
  if (getAudioElement !== undefined) {
    audioElementProvider = getAudioElement;
  }
  if (getAudioLevel !== undefined) {
    audioLevelProvider = getAudioLevel;
  }
  installRealtimeDebugConsoleApi();
}

export async function readInboundAudioStats(peerConnection) {
  if (!peerConnection?.getStats) {
    return null;
  }

  let inbound = null;
  // Diagnostics-only additions (network connectivity investigation, Step 3):
  // RTT and codec are not needed by the tone-anomaly detector this function
  // was originally written for, but they're additional fields read from the
  // SAME single getStats() call below — not a second polling mechanism.
  let selectedCandidatePair = null;
  let codecMimeType = null;
  const stats = await peerConnection.getStats();

  stats.forEach(report => {
    // Chrome reports the live pair as state==='succeeded'; some browsers only
    // set `nominated`. Either flag is enough to call it "the" pair — a peer
    // connection has at most one at a time once ICE has completed.
    if (
      report.type === 'candidate-pair' &&
      (report.state === 'succeeded' || report.nominated === true) &&
      typeof report.currentRoundTripTime === 'number'
    ) {
      selectedCandidatePair = {
        rttMs: Math.round(report.currentRoundTripTime * 1000),
        availableOutgoingBitrate: report.availableOutgoingBitrate ?? null,
      };
    }

    if (report.type !== 'inbound-rtp' || report.kind !== 'audio') {
      return;
    }

    // codecId cross-references a separate 'codec' report — read once we're
    // already iterating this same stats collection, no extra getStats() call.
    if (report.codecId) {
      const codecReport = stats.get?.(report.codecId);
      codecMimeType = codecReport?.mimeType ?? null;
    }

    inbound = {
      packetsReceived: report.packetsReceived ?? 0,
      bytesReceived: report.bytesReceived ?? 0,
      packetsLost: report.packetsLost ?? 0,
      jitter: report.jitter ?? null,
      // > 0 means the received audio is not digital silence. Computed from the
      // *decoded* stream, so it cannot on its own tell a server-sent tone apart
      // from a locally-generated one — see the NetEq counters below for that.
      totalAudioEnergy: report.totalAudioEnergy ?? null,
      audioLevel: report.audioLevel ?? null,

      // NetEq counters. These are what actually discriminate between the three
      // ways a buzz can happen:
      //
      //   concealed/totalSamples climbing with packetsLost > 0
      //     -> packet-loss concealment ringing. Not a local render fault.
      //   inserted/removed samples spiking with packetsLost == 0
      //     -> NetEq chasing a drifting clock. That IS the local render path.
      //   all flat
      //     -> neither; the tone is in what OpenAI sent, or in the output
      //        device. Split those with output_audio_buffer.stopped.
      concealedSamples: report.concealedSamples ?? null,
      concealmentEvents: report.concealmentEvents ?? null,
      totalSamplesReceived: report.totalSamplesReceived ?? null,
      insertedSamplesForDeceleration: report.insertedSamplesForDeceleration ?? null,
      removedSamplesForAcceleration: report.removedSamplesForAcceleration ?? null,
      jitterBufferDelay: report.jitterBufferDelay ?? null,
      jitterBufferEmittedCount: report.jitterBufferEmittedCount ?? null,
      jitterBufferTargetDelay: report.jitterBufferTargetDelay ?? null,
      timestamp: report.timestamp ?? null,
      // Diagnostics-only additions — see the comment above this function.
      codec: codecMimeType,
    };
  });

  // Candidate-pair RTT is independent of whether an inbound-rtp audio report
  // exists yet, but every current caller only reads a non-null `inbound` — so
  // it's merged in here rather than changing the null/non-null contract.
  if (inbound && selectedCandidatePair) {
    inbound.rtt = selectedCandidatePair.rttMs;
    inbound.availableOutgoingBitrate = selectedCandidatePair.availableOutgoingBitrate;
  }

  return inbound;
}

// Fields above that are monotonic counters, and therefore meaningless as a
// point read — only their delta over a window says anything.
const CUMULATIVE_STAT_FIELDS = [
  'packetsReceived',
  'bytesReceived',
  'packetsLost',
  'totalAudioEnergy',
  'concealedSamples',
  'concealmentEvents',
  'totalSamplesReceived',
  'insertedSamplesForDeceleration',
  'removedSamplesForAcceleration',
  'jitterBufferDelay',
  'jitterBufferEmittedCount',
];

let statsRingTimer = null;
let statsRing = [];
let statsRingSize = 0;

/**
 * Diagnostics only (network connectivity investigation, Step 3). Turns one
 * `readInboundAudioStats()` sample — plus the previous ring entry, for a
 * bitrate delta — into a handful of flat records matching the Step 2 data
 * model. Never throws (caller wraps it too, but this is the actual source of
 * the values so it guards its own math as well).
 */
function recordWebRtcDiagnostics(stats, previous) {
  const record = useNetworkDiagnostics.getState().record;

  if (typeof stats.rtt === 'number') {
    record('webrtc', 'rtt', stats.rtt, 'ms');
  }
  if (typeof stats.jitter === 'number') {
    record('webrtc', 'jitter', Math.round(stats.jitter * 1000), 'ms');
  }
  if (typeof stats.packetsLost === 'number') {
    record('webrtc', 'packets_lost', stats.packetsLost, 'count');
  }
  if (stats.codec) {
    record('webrtc', 'codec', stats.codec, 'mime_type');
  }

  if (previous && typeof stats.bytesReceived === 'number') {
    const spanMs = performance.now() - previous.at;
    const byteDelta = stats.bytesReceived - (previous.stats.bytesReceived ?? 0);
    if (spanMs > 0 && byteDelta >= 0) {
      const kbps = Math.round(((byteDelta * 8) / spanMs) * 1000) / 1000;
      record('webrtc', 'inbound_bitrate', kbps, 'kbps');
    }
  }
}

/**
 * Samples inbound RTP stats into a ring so an anomaly can report what the
 * transport was doing in the seconds *before* it — a single read at detection
 * time lands ~1.2s late and carries no baseline to diff against.
 *
 * Idempotent: starting an already-running ring is a no-op rather than a second
 * interval.
 */
export function startInboundStatsRing(getPeerConnection, { intervalMs, size }) {
  const target = getTarget();
  if (!target || statsRingTimer !== null) {
    return;
  }

  statsRing = [];
  statsRingSize = size;

  statsRingTimer = target.setInterval(async () => {
    const stats = await readInboundAudioStats(getPeerConnection?.() ?? null).catch(() => null);
    if (!stats) {
      return;
    }

    const previous = statsRing[statsRing.length - 1] ?? null;
    statsRing.push({ at: performance.now(), stats });
    if (statsRing.length > statsRingSize) {
      statsRing.splice(0, statsRing.length - statsRingSize);
    }

    // Diagnostics only — rides this SAME 1s tick rather than a second timer.
    // Never throws: a failure here must not stop the tone-anomaly ring this
    // interval exists for.
    try {
      recordWebRtcDiagnostics(stats, previous);
    } catch (_error) {
      // Telemetry must never break the tone-anomaly ring.
    }
  }, intervalMs);
}

export function stopInboundStatsRing() {
  const target = getTarget();
  if (statsRingTimer !== null) {
    target?.clearInterval(statsRingTimer);
    statsRingTimer = null;
  }
  statsRing = [];
}

/**
 * Difference between the newest ring entry and the newest entry at or before
 * `sinceMs`. Returns null when the ring does not span the window — better than
 * a delta computed against a sample that is really the same one.
 */
export function readInboundStatsDelta(sinceMs) {
  if (statsRing.length < 2) {
    return null;
  }

  const latest = statsRing[statsRing.length - 1];
  let baseline = null;
  for (let i = statsRing.length - 1; i >= 0; i -= 1) {
    if (statsRing[i].at <= sinceMs) {
      baseline = statsRing[i];
      break;
    }
  }

  if (!baseline || baseline === latest) {
    return null;
  }

  const delta = { spanMs: Math.round(latest.at - baseline.at) };
  CUMULATIVE_STAT_FIELDS.forEach(field => {
    const from = baseline.stats[field];
    const to = latest.stats[field];
    delta[field] = typeof from === 'number' && typeof to === 'number' ? to - from : null;
  });

  // The headline ratio: what share of the samples the decoder had to invent.
  // Above ~2% with packetsLost > 0 is packet-loss concealment, not a local
  // render fault, and no amount of cycling the <audio> element will help.
  delta.concealedRatio =
    typeof delta.concealedSamples === 'number' &&
    typeof delta.totalSamplesReceived === 'number' &&
    delta.totalSamplesReceived > 0
      ? delta.concealedSamples / delta.totalSamplesReceived
      : null;

  // Jitter buffer is not cumulative in a useful way; report the latest target.
  delta.jitterBufferTargetDelay = latest.stats.jitterBufferTargetDelay ?? null;
  delta.jitter = latest.stats.jitter ?? null;

  return delta;
}

/**
 * One call that answers "did audio actually arrive, and if so why can't I hear
 * it". Reads the whole chain in order, so the first `false` is the culprit:
 *
 *   OpenAI sent bytes → a track reached us → the <audio> element has it →
 *   the element is playing → the samples are not silence.
 */
export async function getRealtimeAudioReport() {
  const target = getTarget();
  const snapshot = target?.__realtimeConnectionDebug ?? null;
  const peerConnection = peerConnectionProvider?.() ?? null;
  const audioElement = audioElementProvider?.() ?? null;
  const inboundAudio = await readInboundAudioStats(peerConnection);
  const events = target?.__realtimeEvents ?? [];

  const eventCounts = events.reduce((counts, entry) => {
    counts[entry.type] = (counts[entry.type] ?? 0) + 1;
    return counts;
  }, {});

  return {
    connection: {
      status: snapshot?.status ?? null,
      callId: snapshot?.callId ?? null,
      error: snapshot?.error ?? null,
      // Non-empty means the hook never even attempted to connect.
      gateBlockedBy: snapshot?.gateBlockedBy ?? null,
      playbackBlocked: snapshot?.playbackBlocked ?? null,
      connectionState: peerConnection?.connectionState ?? snapshot?.connectionState ?? null,
      iceConnectionState:
        peerConnection?.iceConnectionState ?? snapshot?.iceConnectionState ?? null,
      iceGatheringState: peerConnection?.iceGatheringState ?? snapshot?.iceGatheringState ?? null,
      reconnecting: snapshot?.reconnecting ?? false,
      reconnectAttempt: snapshot?.reconnectAttempt ?? 0,
    },
    // Straight from the transport: bytes here with silence in the room means the
    // problem is local playback, not OpenAI.
    inboundAudio,
    audioElement: describeAudioElement(audioElement),
    // Last RMS the monitor measured — the browser's own view of loudness.
    monitor: {
      lastRms: audioLevelProvider?.() ?? null,
      audibleNow: snapshot?.audioSignalPresent ?? false,
      lastTonePeakHz: snapshot?.lastTonePeakHz ?? null,
      lastToneProminenceDb: snapshot?.lastToneProminenceDb ?? null,
      toneAnomalyCount: snapshot?.toneAnomalyCount ?? 0,
      tickLagMs: snapshot?.tickLagMs ?? null,
    },
    openAiErrors: {
      last: snapshot?.lastOpenAiErrorCode ?? null,
      counts: snapshot?.openAiErrorCounts ?? null,
    },
    events: {
      total: events.length,
      byType: eventCounts,
      last: events.slice(-10).map(entry => ({ at: entry.at, type: entry.type })),
    },
  };
}

function installRealtimeDebugConsoleApi() {
  const target = getTarget();
  if (!target || target.__realtimeAudioReport) {
    return;
  }

  target.__realtimeAudioReport = async () => {
    const report = await getRealtimeAudioReport();
    console.warn('[realtime] audio report', report);
    return report;
  };

  // Polls the report so a silence that starts mid-session is visible without
  // hammering the console by hand. Returns its own stop function.
  target.__realtimeAudioWatch = (intervalMs = 2000) => {
    const timer = target.setInterval(async () => {
      const report = await getRealtimeAudioReport();
      console.warn('[realtime] watch', {
        state: report.connection.connectionState,
        bytesReceived: report.inboundAudio?.bytesReceived ?? null,
        totalAudioEnergy: report.inboundAudio?.totalAudioEnergy ?? null,
        paused: report.audioElement?.paused ?? null,
        muted: report.audioElement?.muted ?? null,
        rms: report.monitor.lastRms,
      });
    }, intervalMs);

    return () => target.clearInterval(timer);
  };

  target.__realtimeEventsOfType = type =>
    (target.__realtimeEvents ?? []).filter(entry => entry.type === type).map(entry => entry.event);
}
